import { PerformedSearchRequestContext } from 'clientState/providers/SearchRequestProvider';
import { SegmentFragment } from 'generated/graphql';
import { FilterTransportType } from '../Provider/types';

const MAX_CONNECTIONS_THRESHOLD = 2;

export const DONT_CARE_OPTION = 'DONT_CARE';
export const NO_STOPS_OPTION = '0';

type ConnectionThreshold = {
  upto: string;
  disabled: boolean;
};
const getDefaultValue = (
  connectionThresholds: ConnectionThreshold[],
  resetValue: string,
  withoutConnections?: boolean
) => {
  if (
    withoutConnections &&
    connectionThresholds.some(({ upto }) => upto === NO_STOPS_OPTION)
  ) {
    return NO_STOPS_OPTION;
  }
  return resetValue;
};

const getConnectionThresholds = (
  connectionThresholds: ConnectionThreshold[],
  withoutConnections?: boolean
) => {
  const [{ upto: resetValue }] = connectionThresholds;
  return {
    values: connectionThresholds,
    resetValue,
    defaultValue: getDefaultValue(
      connectionThresholds,
      resetValue,
      withoutConnections
    ),
  };
};

type ConnectionThresholdCollectorReturnType = {
  add: (segment: SegmentFragment) => void;
  build: () => {
    values: ConnectionThreshold[];
    resetValue: string;
    defaultValue: string;
  };
};

export const ConnectionThresholdCollector = (
  withoutConnections?: boolean
): ConnectionThresholdCollectorReturnType => {
  const connectionThresholdSet = new Set<number>();
  let dontCareOption = false;
  return {
    add({ sections }) {
      const numConnections = sections.length - 1;
      if (numConnections > MAX_CONNECTIONS_THRESHOLD) {
        dontCareOption = true;
      } else {
        connectionThresholdSet.add(numConnections);
      }
    },
    build() {
      const connectionThresholds = [...connectionThresholdSet]
        .sort((a, b) => a - b)
        .map((upto) => ({
          upto: upto.toString(),
          disabled: false,
        }));
      if (connectionThresholds.length > 1 || dontCareOption) {
        connectionThresholds.unshift({
          upto: DONT_CARE_OPTION,
          disabled: false,
        });
      }
      return getConnectionThresholds(connectionThresholds, withoutConnections);
    },
  };
};

const ConnectionThresholdsByLegCollector = ({
  tripType,
  withoutConnections,
}: PerformedSearchRequestContext) => {
  const byLeg: ConnectionThresholdCollectorReturnType[] = [];

  return {
    add(legId: number, segment: SegmentFragment) {
      byLeg[legId] =
        byLeg[legId] || ConnectionThresholdCollector(withoutConnections);
      byLeg[legId].add(segment);
    },
    build() {
      const connectionsThresholdsByLeg = byLeg.map((collector) =>
        collector.build()
      );
      if (
        tripType === 'ROUND_TRIP' &&
        connectionsThresholdsByLeg[0] &&
        connectionsThresholdsByLeg[1]
      ) {
        return [
          joinRoundTripMaxConnections(
            connectionsThresholdsByLeg[0],
            connectionsThresholdsByLeg[1],
            withoutConnections
          ),
        ];
      }
      return connectionsThresholdsByLeg;
    },
  };
};

const getConnectionThresholdMap = (
  connectionThresholds: ReturnType<
    ConnectionThresholdCollectorReturnType['build']
  >
) =>
  connectionThresholds?.values.reduce(
    (result, connectionThreshold) =>
      result.set(connectionThreshold.upto, connectionThreshold),
    new Map<string, ConnectionThreshold>()
  );

const getRangeConnectionOptions = (
  connectionThresholds: ReturnType<
    ConnectionThresholdCollectorReturnType['build']
  >
) =>
  connectionThresholds?.values.reduce(
    (range, { upto }) => {
      if (upto === DONT_CARE_OPTION) {
        return range;
      }
      return {
        min: Math.min(parseInt(upto), range.min),
        max: Math.max(parseInt(upto), range.max),
      };
    },
    {
      min: Infinity,
      max: 0,
    }
  );

const joinRoundTripMaxConnections = (
  outbound: ReturnType<ConnectionThresholdCollectorReturnType['build']>,
  inbound: ReturnType<ConnectionThresholdCollectorReturnType['build']>,
  withoutConnections?: boolean
) => {
  const outboundConnectionThresholds = getConnectionThresholdMap(outbound);
  const inboundConnectionThresholds = getConnectionThresholdMap(inbound);
  const outboundRange = getRangeConnectionOptions(outbound);
  const inboundRange = getRangeConnectionOptions(inbound);
  const min = Math.max(outboundRange.min, inboundRange.min);
  const max = Math.max(outboundRange.max, inboundRange.max);
  const getUpToConnectionOption = (connectionOptionId: string) =>
    outboundConnectionThresholds.get(connectionOptionId) ||
    inboundConnectionThresholds.get(connectionOptionId);

  const connectionThresholds: ConnectionThreshold[] = [];
  for (let index = min; index <= max; ++index) {
    const upto = `${index}`;
    const upToConnectionOption = getUpToConnectionOption(upto);
    if (upToConnectionOption) {
      connectionThresholds.push(upToConnectionOption);
    }
  }

  if (
    connectionThresholds.length > 1 ||
    getUpToConnectionOption(DONT_CARE_OPTION)
  ) {
    connectionThresholds.unshift({
      ...connectionThresholds[0],
      upto: DONT_CARE_OPTION,
      disabled: false,
    });
  }
  return getConnectionThresholds(connectionThresholds, withoutConnections);
};

type ConnectionThresholdsByTransportType = Record<
  FilterTransportType,
  ReturnType<typeof ConnectionThresholdsByLegCollector>
>;

const ConnectionThresholdsByTransportTypeAndLegCollector = (
  context: PerformedSearchRequestContext
) => {
  const collectors: ConnectionThresholdsByTransportType = {
    '': ConnectionThresholdsByLegCollector(context),
    TRAIN: ConnectionThresholdsByLegCollector(context),
    PLANE: ConnectionThresholdsByLegCollector(context),
  };

  return {
    add: (legId: number, segment: SegmentFragment) => {
      collectors[''].add(legId, segment);
      const transportType = segment.transportTypes[0];
      if (transportType === 'TRAIN') {
        collectors.TRAIN.add(legId, segment);
      } else if (transportType === 'PLANE') {
        collectors.PLANE.add(legId, segment);
      }
    },
    build: () => {
      return {
        '': collectors[''].build(),
        TRAIN: collectors.TRAIN?.build(),
        PLANE: collectors.PLANE?.build(),
      };
    },
  };
};

export default ConnectionThresholdsByTransportTypeAndLegCollector;
